import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CardLightConstants } from '../float-card/card-light/card-light.constants';
import { EventConstants } from '../../../../app/constants/event.constants';
import { environment } from '../../../../environments/environment';
import { DemoAccountService } from '../../../../app/services/demo-account.service';
import { NavigationService } from '../../../../app/services/navigation.service';
import { CreateDemoAccountGQL, CreateDemoUserGQL, GetChildTreeForOrgIdGQL, GetSelfRegOrgsGQL } from '../../../../app/core/graphql/generated/graphql';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { DropdownItem } from '../../../../app/core/models/dropdown.model';
import { DEMO_ACCOUNT_COUNTRY, DEMO_ACCOUNT_OTHERS, ErrorMessages, PasswordRules, USER } from './demo-float-card.constants';
import { ButtonLabels } from '../float-card/card-dark/card-dark.constants';
import { REGEX_PATTERNS } from './validators/regex-pattern-validator';
import { PasswordValidator } from './validators/password-validator';
import { RouteConstants } from '../../../../app/constants/route.constants';
import { SubOrganizations } from '../../../../app/core/models/sub-organization.model';
import { StatusMessageConfigInterface } from '@savvaslearning/cel-components/dist-stencil/interfaces/components';

@Component({
    selector: 'app-demo-float-card',
    templateUrl: './demo-float-card.component.html',
    styleUrls: ['./demo-float-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DemoFloatCardComponent implements OnInit {
    logoImageRole: string;
    setupDemoAccountTitle: string;
    setupDemoAccountInstructions: string;
    iconShowed: boolean;
    displayModal: boolean;
    linkClicked: boolean = false;
    hasAcceptedTerms: boolean = false;
    @ViewChild('conditionLink') conditionsLink: ElementRef;
    siteKey: string = environment.v2InvisibleRecaptchaSiteKey;
    demoAccountConditions: string;
    @Output() private onFormGroupChange = new EventEmitter<FormGroup>();
    @ViewChild('organizationSelect') organizationSelectElement: ElementRef;
    @ViewChild('emailElem') emailElem: ElementRef;
    @ViewChild('pasword') pasword: ElementRef;
    @ViewChild('org') org: ElementRef;
    dropdownOrganizations: DropdownItem[] = [];
    demoForm: FormGroup;
    createDemoAccountButtonLabel: string;
    emailErrorMessage: string;
    passwordErrorMessage: string;
    passwordRules = PasswordRules;
    orgError = false;
    emailError = false;
    passwordError = false;
    orgStyle: HTMLStyleElement;
    emailStatusConfig: StatusMessageConfigInterface = {
        errorMessages: [
            {
                identifier: 'email-error',
                message: 'Not a valid email address'
            }
        ],
    }
    passwordStatusConfig: StatusMessageConfigInterface = {
        errorMessages: [
            {
                identifier: 'password-error',
                message: 'Password does not meet requirements'
            }
        ],
        customMessages: [
            {
                identifier: 'password-case',
                message: '• Passwords are case-sensitive',
            },
            {
                identifier: 'password-length',
                message: '• Must be at least 8 characters long',
            },
            {
                identifier: 'password-letter',
                message: '• Must contain at least one letter',
            },
            {
                identifier: 'password-special',
                message: '• Must contain at least one number or one special character and cannot contain the word "demo"',
            },
            {
                identifier: 'password-no-spaces',
                message: '• No spaces',
            }
        ],
        successMessages: []
    }
    orgStatusConfig: StatusMessageConfigInterface = {
        errorMessages: [
            {
                identifier: 'org-error',
                message: 'Select organization'
            }
        ]
    }

    constructor(
        private demoAccountService: DemoAccountService,
        private navigationService: NavigationService,
        private getSelfRegOrgsService: GetSelfRegOrgsGQL,
        private getChildTreeForOrgIdService: GetChildTreeForOrgIdGQL,
        private createDemoAccountService: CreateDemoAccountGQL,
        private createDemoUserService: CreateDemoUserGQL,
    ) { this.demoAccountService.termsCheck$.subscribe (response => this.linkClicked= response) }

    ngOnInit(): void {
        this.demoAccountService.termsCheck$.subscribe ((response) => this.linkClicked= response)
        this.logoImageRole =
                window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1 ||
                window.navigator.userAgent.toLowerCase().indexOf('cros') > -1
                    ? 'image'
                    : 'img';
        
        this.initializeConstants();
        this.orgStyle = document.createElement('style');
        this.orgStyle.textContent = '.single-item--selected { background-color: #cdcdcd !important; }';
        this.demoAccountService.termsCheck$.subscribe (response => this.linkClicked= response)

        this.emailErrorMessage = ErrorMessages.INVALID_EMAIL;
        this.passwordErrorMessage = ErrorMessages.INVALID_PASSWORD;
        this.createDemoAccountButtonLabel = ButtonLabels.CREATE_DEMO_ACCOUNT;
        this.demoForm = this.createDemoForm();
        this.getOrganizationsFromChildTree();

        this.demoForm.valueChanges.subscribe((value) => {
            this.onFormGroupChange.emit(this.demoForm);
        });
        this.demoAccountService.recaptchaResponse$.subscribe((response) => {
            this.demoForm.controls['recaptcha_response'].setValue(response);
        });
        this.demoAccountService.termsAndConditions$.subscribe((response) => {
            this.hasAcceptedTerms = response;
        });

        if (this.isMobileSafari()) {
            setTimeout( () => {
                this.org?.nativeElement.setAttribute("tabindex","0");
            }, 100);
        }
    }

    openModal(): void {
        this.displayModal = true;
    }
        
    closeModal(event: Event): void {
        this.linkClicked = true;
        const link = this.conditionsLink?.nativeElement?.shadowRoot?.querySelector('a');
        link?.focus();

        const type = (event as CustomEvent).type;
        this.displayModal = false;
        if (type == EventConstants.TYPE_ACCEPT) {
            this.iconShowed = true;
        }
    }

    initializeConstants(): void {
        this.setupDemoAccountTitle = CardLightConstants.DEMO.TITLE;
        this.setupDemoAccountInstructions = CardLightConstants.DEMO.INSTRUCTIONS;
        this.demoAccountConditions = CardLightConstants.DEMO.DEMO_ACCOUNT_CONDITIONS;
    }

    resolved(captchaResponse: string): void {
        this.demoAccountService.setRecaptchaResponse(captchaResponse);
        this.onSubmit();
    }

    handleCaptchaError(): void {
        console.log('Captcha error');
    }

    newTerms(){
        this.demoAccountService.setTermsCheck(true)
    }

    createDemoForm(): FormGroup {
        return new FormGroup({
            email: new FormControl('', [
                Validators.required,
                Validators.email,
                Validators.pattern(REGEX_PATTERNS.emailPattern),
            ]),
            password: new FormControl('', [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern(REGEX_PATTERNS.passwordPattern),
                PasswordValidator.alphabetic,
                PasswordValidator.noSpaces,
            ]),
            orgId: new FormControl('', [Validators.required]),
            recaptcha_response: new FormControl('',),
        });
    }

    onSubmit(): void {
        if (this.demoForm.invalid || !this.hasAcceptedTerms) {
            this.emailError = this.isEmailError();
            this.passwordError = this.isPasswordError();
            this.handleOrgBlur();
            this.newTerms();
            return; // button is disabled...
        }
        this.createDemoUserService.mutate({ userDetails: this.demoForm?.value }).subscribe(
            (res) => {
                const userId = res.data?.createDemoUser?.userId;
                const userName = res.data?.createDemoUser?.userName;
                if (userId && userName) {
                    sessionStorage.setItem('userId', userId);
                    sessionStorage.setItem('userName', userName);
                    this.navigationService.navigateToRoute(RouteConstants.SUCCESS_PAGE);
                } else {
                    this.navigationService.navigateToRoute(RouteConstants.ERROR_PAGE);
                }
            },
            (catchError) => {
                this.navigationService.navigateToRoute(RouteConstants.ERROR_PAGE);
            }
        );
    }

    getControl(controlName: string) {
        return this.demoForm.controls[controlName];
    }

    getOrganizationsFromChildTree(): void {
        this.getChildTreeForOrgIdService.fetch({orgId: environment.orgId}).subscribe((result) => {
            this.dropdownOrganizations = this.setChildOrganizations(result?.data?.getChildTreeForOrgId?.subOrganizations);
        });
    }

    setChildOrganizations(organizations: SubOrganizations[]): DropdownItem[] {
        let canada = false;
        const subOrganizationsList = [...organizations];
        const otherRegionList: DropdownItem[] = [];
        const canadaRegionList: DropdownItem[] = [];
        subOrganizationsList.sort((a,b) => a?.displayName.localeCompare(b?.displayName));
        subOrganizationsList.forEach((org) => {
            if (!(org?.displayName?.toLowerCase()?.includes(DEMO_ACCOUNT_OTHERS.DISTRICT))) {
                if (org?.country === DEMO_ACCOUNT_COUNTRY.US) {
                    otherRegionList.push({
                        label: org?.displayName,
                        identifier: org?.organizationId,
                        disabled: false
                    })
                } else if(org?.country === DEMO_ACCOUNT_COUNTRY.CA) {
                    if (!canada) {
                        canadaRegionList.push({
                            label: DEMO_ACCOUNT_OTHERS.CA_LABEL,
                            identifier: DEMO_ACCOUNT_OTHERS.CA_IDENTIFIER,
                            disabled: true
                        })
                        canada = true;
                    }
                    canadaRegionList.push({
                        label: org?.displayName,
                        identifier: org?.organizationId,
                        disabled: false
                    })
                }
            }
        });
        canadaRegionList.forEach((entry) => {
            otherRegionList.push({
                label: entry?.label,
                identifier: entry?.identifier,
                disabled: entry?.disabled
            })
        });
        return otherRegionList;
    }
    selectOrganization(event: Event) {
        const org = (<CustomEvent>event).detail.identifier;
        this.demoForm.controls['orgId'].setValue(org);
    }

    get email(): AbstractControl | null {
        sessionStorage.setItem(USER.USER_EMAIL_ADDRESS, this.demoForm.get('email')?.value);
        return this.demoForm.get('email');
    }

    get password(): AbstractControl | null {
        return this.demoForm.get('password');
    }

    get organization(): AbstractControl | null {
        return this.demoForm.get('organization');
    }

    handleEmailBlur(): void {
        this.emailError = this.isEmailError();
        setTimeout(() => {
            const errorElement = this.emailElem?.nativeElement?.shadowRoot?.querySelector('.error-container');
            const isRoleExists = errorElement?.getAttribute('role');
            if (this.email?.invalid && isRoleExists == null) {
                errorElement?.setAttribute('role', 'alert');
                errorElement?.setAttribute('aria-live', 'true');
            }
        }, 5);
    }

    handlePaswordBlur(): void {
        this.passwordError = this.isPasswordError();
        setTimeout(() => {
            const errorElement = this.pasword?.nativeElement?.shadowRoot?.querySelector('.error-container');
            const isRoleExists = errorElement?.getAttribute('role');
            if (this.password?.invalid && isRoleExists == null) {
                errorElement?.setAttribute('role', 'alert');
                errorElement?.setAttribute('aria-live', 'true');
            }
        }, 5);
    }
    handleOrgBlur(): void {
        this.orgError = false;
        setTimeout(() => {
            this.orgError = this.demoForm.get('orgId')?.value?.length > 0 ? false : true;
        }, 200);
    }
    handleOrgClick(): void {
        setTimeout(() => {
            const selectedOrgElem = this.org?.nativeElement?.shadowRoot?.querySelector(
                '[data-identifier="' + this.demoForm.get('orgId')?.value + '"]'
            );
            if (selectedOrgElem) {
                selectedOrgElem?.shadowRoot?.appendChild(this.orgStyle);
            }
        }, 0);
    }

    isMobileSafari(): boolean {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    isEmailError(): boolean {
        return this.email?.invalid? true : false;
    }

    isPasswordError(): boolean {
        return this.password?.invalid? true : false;
    }
}